import { WithdrawalsQueryKeys } from '../../queryKeys';
import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';
import { fetchFee } from './service';
import { UseFeeProps, UseFeeQueryKey } from './props';
import { FeeResponseDto } from '@starsoft/common/dto';
import { GenericError } from '@starsoft/common/models';
import { useAxiosInstance } from '@starsoft/common/hooks';

/**
 * Custom hook to fetch withdrawal fee information
 *
 * This hook manages the query state for fetching withdrawal fees based on provided parameters.
 * It uses React Query to handle caching, retries, and refetching behavior.
 * The fee data is considered fresh indefinitely (staleTime: Infinity) but will refetch when reconnecting.
 *
 * @param {UseFeeProps} props - The props object containing:
 *   @param {string} [props.chainId] - The blockchain network ID for crypto withdrawals
 *   @param {string} [props.coinId] - The cryptocurrency ID
 *   @param {string} [props.methodId] - The withdrawal method ID (required for query to be enabled)
 *   @param {number} [props.amount] - The withdrawal amount
 *   @param {boolean} [props.enabled=true] - Whether the query should be enabled
 *
 * @returns {Object} An object containing:
 *   - fee: The fee data from the API response
 *   - isLoading: Boolean indicating if fee data is being fetched
 *   - error: Any error that occurred during the fetch
 *   - refetch: Function to manually trigger a refetch
 *   - isError: Boolean indicating if an error occurred
 */
export function useFee({
  chainId,
  coinId,
  methodId,
  amount,
  enabled = true,
}: UseFeeProps) {
  const axiosInstance: AxiosInstance = useAxiosInstance();
  const queryKey: UseFeeQueryKey = [
    WithdrawalsQueryKeys.Fee,
    {
      chainId,
      coinId,
      amount,
      methodId,
    },
  ];

  const { data, isPending, error, refetch, isError, fetchStatus } = useQuery<
    FeeResponseDto,
    AxiosError<GenericError>,
    FeeResponseDto,
    UseFeeQueryKey
  >({
    enabled: enabled && !!methodId,
    queryKey,
    retry: 1,
    queryFn: queryContext => fetchFee(queryContext, axiosInstance),
    staleTime: Infinity,
    refetchOnReconnect: 'always',
  });

  return {
    fee: data,
    isLoading: isPending && fetchStatus !== 'idle',
    error,
    refetch,
    isError,
  };
}
